import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import MovieFilterPanel from "@/components/feature/MovieFilterPanel";
import SortBar from "@/components/feature/SortBar";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeader from "@/components/feature/SectionHeader";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterOptions } from "@/mocks/site";
import {
  filterMovies,
  sortMovies,
  paginate,
  pageSize,
  searchMovies,
  getPopularMovies,
  type MovieFilters,
  type SortKey
} from "@/utils/content";

const emptyFilters: MovieFilters = {
  type: "全部",
  region: "全部",
  year: "全部",
  rating: "全部"
};

const hotKeywords = ["科幻", "悬疑", "国风", "纪录片", "治愈", "2024"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [inputValue, setInputValue] = useState(keyword);
  const [filters, setFilters] = useState<MovieFilters>(emptyFilters);
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta({
    title: keyword ? `搜索“${keyword}” - 秘密影院` : "搜索影视 - 秘密影院",
    description: keyword
      ? `秘密影院为你呈现“${keyword}”相关的影视搜索结果，支持按类型、地区、年代与评分进一步筛选，快速找到想看的作品。`
      : "在秘密影院搜索电影、电视剧、动漫、综艺与纪录片，输入关键词即可快速找到你想看的影视作品。",
    keywords: keyword
      ? `${keyword},秘密影院搜索,影视搜索,${keyword}在线观看`
      : "影视搜索,秘密影院,电影搜索,电视剧搜索"
  });

  useEffect(() => {
    setInputValue(keyword);
    setPage(1);
  }, [keyword]);

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    const matched = searchMovies(keyword);
    const filtered = filterMovies(matched, filters);
    return sortMovies(filtered, sortKey);
  }, [keyword, filters, sortKey]);

  const totalPages = Math.max(1, Math.ceil(results.length / pageSize));
  const pageItems = paginate(results, Math.min(page, totalPages));
  const hasKeyword = keyword.trim().length > 0;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = inputValue.trim();
    if (next) {
      setSearchParams({ q: next });
    } else {
      setSearchParams({});
    }
  };

  const handleFilterChange = (key: keyof MovieFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(emptyFilters);
    setPage(1);
  };

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <header className="mb-8">
        <h1 className="font-heading text-2xl font-800 text-foreground-50 md:text-3xl">
          搜索影视
        </h1>
        <p className="mt-2 text-sm text-foreground-400">
          输入片名、类型、导演或演员，快速定位你想看的作品
        </p>

        <form onSubmit={submit} className="mt-5 max-w-2xl" role="search">
          <div className="flex items-center gap-2 rounded-full border border-background-700 bg-background-900 px-4 py-2.5 transition-colors focus-within:border-secondary-500">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line" aria-hidden="true" />
            </span>
            <input
              type="search"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="例如：科幻、悬疑、星海纪年……"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none md:text-base"
            />
            <button
              type="submit"
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-1.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-foreground-500">热门搜索：</span>
          {hotKeywords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => {
                setInputValue(word);
                setSearchParams({ q: word });
              }}
              className="cursor-pointer rounded-full border border-background-700 bg-background-900 px-3 py-0.5 text-xs text-foreground-300 transition-colors hover:border-secondary-500 hover:text-secondary-300"
            >
              {word}
            </button>
          ))}
        </div>
      </header>

      {!hasKeyword ? (
        <section>
          <SectionHeader title="热门影视推荐" subtitle="还没有搜索？先看看大家都在关注什么" />
          <MovieGrid movies={getPopularMovies(8)} />
        </section>
      ) : (
        <div className="flex gap-8">
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-20 rounded-lg border border-background-800 bg-background-900/70 p-5">
              <h3 className="mb-5 font-heading text-base font-600 text-foreground-50">
                筛选条件
              </h3>
              <MovieFilterPanel
                options={filterOptions}
                value={filters}
                onChange={handleFilterChange}
                onReset={handleReset}
              />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <SortBar
              sortKey={sortKey}
              onChange={(key) => {
                setSortKey(key);
                setPage(1);
              }}
              onOpenFilter={() => setMobileFilterOpen(true)}
              resultText={`“${keyword}” 共找到 ${results.length} 个结果`}
            />

            {pageItems.length > 0 ? (
              <>
                <MovieGrid movies={pageItems} />
                <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="space-y-10">
                <EmptyState
                  icon="ri-search-eye-line"
                  title={`没有找到与“${keyword}”相关的影视`}
                  description="试试更换关键词，或放宽筛选条件。你也可以从下面的热门推荐开始浏览。"
                  action={
                    <div className="flex flex-wrap justify-center gap-3">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 text-sm font-600 text-foreground-200 transition-colors hover:border-secondary-500 hover:text-secondary-300"
                      >
                        重置筛选条件
                      </button>
                      <Link
                        to="/category/all"
                        className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
                      >
                        浏览全部影视
                      </Link>
                    </div>
                  }
                />
                <section>
                  <SectionHeader title="也许你会喜欢" />
                  <MovieGrid movies={getPopularMovies(4)} />
                </section>
              </div>
            )}
          </div>
        </div>
      )}

      {mobileFilterOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-background-950/80 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={() => setMobileFilterOpen(false)}
        >
          <div
            className="max-h-[80vh] w-full overflow-y-auto rounded-t-lg border-t border-background-700 bg-background-900 p-5 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-heading text-base font-600 text-foreground-50">
                筛选条件
              </h3>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-300 hover:text-accent-400"
              >
                <i className="ri-close-line text-lg" aria-hidden="true" />
              </button>
            </div>
            <MovieFilterPanel
              options={filterOptions}
              value={filters}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md bg-accent-500 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
            >
              查看结果（{results.length} 个）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}